/**
 * Who else is here, as in Miro: everyone connected to the same core, with a name and a colour,
 * where their pointer is on the graph and what they selected.
 *
 * On every (re)connection this page joins with the name kept in the browser (a "Guest" name
 * the first time). Then it tells the core, through `presence.*` requests:
 * - where the pointer is on the graph, in world coordinates, at most every {@link CURSOR_MS};
 * - what is selected, whenever the selection changes (read from the view).
 *
 * The core says who this page is (`you`); the page never chooses it. A core without the
 * `presence` module (an older one) simply leaves the list empty.
 */
import { createEffect, createSignal, type Accessor } from "solid-js";
import type { Cursor } from "@protocol/Cursor";
import type { Person } from "@protocol/Person";
import type { Selected } from "@protocol/Selected";
import { isClientError, type Client } from "../client/client";
import { load, save } from "../shared/storage";
import { throttle } from "../shared/throttle";
import { toast } from "../shared/toast";
import type { Selection, View } from "./view";

/** The fewest milliseconds between two cursor messages (about 20 a second). */
export const CURSOR_MS = 50;

/**
 * How quickly another person's cursor catches up with where they really are: after this many
 * milliseconds it has covered about two thirds of the way. Cursor messages arrive at most every
 * {@link CURSOR_MS}, so gliding over a bit more than that hides the steps between them.
 */
export const CURSOR_GLIDE_MS = 70;

/** A point on the graph, in world coordinates. */
export interface WorldPoint {
  /** World position. */
  x: number;
  /** World position. */
  y: number;
  /** Depth (0 in 2D). */
  z: number;
}

/**
 * Moves a shown cursor part of the way towards where it should be, after `dtMs` milliseconds:
 * fast when far, slowing down as it arrives, and the same speed whatever the frame rate.
 */
export function glide(shown: WorldPoint, target: WorldPoint, dtMs: number, glideMs: number = CURSOR_GLIDE_MS): WorldPoint {
  const t = 1 - Math.exp(-Math.max(dtMs, 0) / glideMs);
  return { x: shown.x + (target.x - shown.x) * t, y: shown.y + (target.y - shown.y) * t, z: shown.z + (target.z - shown.z) * t };
}

/** Where this browser keeps the chosen name and colour. */
export const ME_KEY = "developer-app-me-v1";

/** The name and colour this browser joins with. */
export interface Me {
  /** The name shown to the others. */
  name: string;
  /** `#rrggbb`, or empty to let the core pick one. */
  colour: string;
}

/** Everyone connected, and what this page tells the others. */
export interface PresenceService {
  /** Everyone who joined, in the order they joined (this page included). */
  people: Accessor<Person[]>;
  /** This page's id, as the core named it (`null` until joined). */
  you: Accessor<string | null>;
  /** Everyone except this page. */
  others: Accessor<Person[]>;
  /** Where each person's pointer is (kept apart so a cursor move redraws only that cursor). */
  cursors: Accessor<Record<string, Cursor | null>>;
  /** The name and colour this browser joins with. */
  me: Accessor<Me>;
  /** Changes the name (and colour) for everyone; remembered in this browser. */
  rename(name: string, colour?: string): Promise<boolean>;
  /** Where this page's pointer is on the graph, in world coordinates (`null`: off the graph). */
  moveCursor(cursor: Cursor | null): void;
}

/** What {@link createPresence} is built from. */
export interface PresenceNeeds {
  /** The connection to the core. */
  client: Client;
  /** The selection, sent to the others when it changes. */
  view: View;
}
/** A selection as the others see it (`null` for nothing, and for several nodes: one ring each is not shared yet). Links are `from>to>kind`. */
/** A selection as the others see it (`null` for nothing). Links are `from>to>kind`. */
export function selectedOf(selection: Selection): Selected | null {
  switch (selection?.type) {
    case "node":
      return { kind: "node", id: selection.id };
    case "group":
      return { kind: "group", id: selection.id };
    case "link":
      return { kind: "link", id: `${selection.link.from}>${selection.link.to}>${selection.link.kind}` };
    case "bundle":
      return { kind: "bundle", id: selection.key };
    default:
      return null;
  }
}

/** One or two letters for a person's badge: "Ann Lee" → "AL", "bob" → "B". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]!, words[words.length - 1]!] : words;
  return letters.map((w) => Array.from(w)[0] ?? "").join("").toUpperCase() || "?";
}

/** A name for someone who never chose one: "Guest 1234". */
export function guestName(random: () => number = Math.random): string {
  return `Guest ${1000 + Math.floor(random() * 9000)}`;
}

/** Starts telling the core who this page is and following everyone else. */
export function createPresence({ client, view }: PresenceNeeds): PresenceService {
  const [people, setPeople] = createSignal<Person[]>([]);
  const [you, setYou] = createSignal<string | null>(null);
  const [cursors, setCursors] = createSignal<Record<string, Cursor | null>>({});
  const stored = load<Partial<Me>>(ME_KEY, {});
  const [me, setMe] = createSignal<Me>({ name: stored.name?.trim() || guestName(), colour: stored.colour ?? "" });
  save(ME_KEY, me());
  /** Whether the core has the `presence` module (an older core does not). */
  let available = true;

  /** Takes a new list of people (and their cursors). */
  const take = (list: Person[]) => {
    setPeople(list);
    setCursors(Object.fromEntries(list.map((p) => [p.id, p.cursor])));
  };

  /** Sends a presence request; remembers when the core has no presence module. */
  const send = async (request: Parameters<Client["request"]>[0]) => {
    if (!available) return null;
    try {
      return await client.request(request);
    } catch (error) {
      if (isClientError(error) && error.code === "module_unavailable") available = false;
      return null;
    }
  };

  /** Joins with this browser's name; then tells the others what is selected. */
  const join = async () => {
    available = true;
    const answer = await send({ method: "presence.join", params: { name: me().name, colour: me().colour } });
    if (answer?.type !== "presence") return;
    setYou(answer.presence.you);
    take(answer.presence.people);
    const selection = selectedOf(view.state.selection);
    if (selection) void send({ method: "presence.select", params: { selection } });
  };
  client.onOpen(() => void join());

  client.onEvent((event) => {
    if (event.event === "presence.changed") take(event.data.people);
    if (event.event === "presence.cursor") setCursors((all) => ({ ...all, [event.data.person]: event.data.cursor }));
  });

  // The selection, whenever it changes (after joining: before, the core would refuse it).
  createEffect(() => {
    const selection = selectedOf(view.state.selection);
    if (you()) void send({ method: "presence.select", params: { selection } });
  });

  const moveCursor = throttle<Cursor | null>(CURSOR_MS, (cursor) => {
    if (you()) void send({ method: "presence.cursor", params: { cursor } });
  });

  return {
    people,
    you,
    others: () => people().filter((p) => p.id !== you()),
    cursors,
    me,
    moveCursor,
    async rename(name, colour = me().colour) {
      const next = { name: name.trim(), colour };
      try {
        const answer = await client.request({ method: "presence.join", params: next });
        if (answer.type === "presence") take(answer.presence.people);
        setMe(next);
        save(ME_KEY, next);
        return true;
      } catch (error) {
        toast(isClientError(error) ? error.message : String(error), "error");
        return false;
      }
    },
  };
}
